import { Injectable } from '@angular/core';
import { ContentService } from '@blk/cwp-ng-framework';
import { Alignment, ColumnComposition, TextRenderer } from '@blk/simba';
import { CellData, CellUtil, TabData } from '@ddt-web/data-service';
import { PortfolioSubstitutionAlerts } from '@ddt-web/domain';
import { HoldingsUtil } from '@ddt-web/generic-utils';
import { IconAndTextRenderer } from '@ddt-web/pdf-utils';
import * as flatten from 'lodash/flatten';
import * as get from 'lodash/get';
import * as isEmpty from 'lodash/isEmpty';
import * as keys from 'lodash/keys';


@Injectable({ providedIn: 'root' })
export class PartnerHoldingsAlertsService {

	constructor(private contentService: ContentService) { }
	private getCellRawValue(securityCells: CellData[], cellName): string {
		return get(CellUtil.getById(securityCells, cellName), 'rawValue', '') as string;
	}

	public populateNotTradableSecuritiesList(portfolioFlagsTabData: TabData): string[] {
		const cells = get(portfolioFlagsTabData, 'tableData[0].cells', []);
		const alertFlags = cells.map(portfolioCellData => get(CellUtil.getById(portfolioCellData, 'securityFlags'), 'rawValue', [])).filter(security => security);
		return flatten(alertFlags);
	}

	/**
	 * Get Substitution Alerts List for multiple portfolio
	 *
	 * invalidSecuritiesAlerts - alert message array for invalidSecurities replacements
	 * assetClassAlerts - alert message array for asset Class Substitutions
	 *
	 * @param {*} securitiesTabData
	 * @param {ContentService} contentService
	 * @returns {SubstitutedFromAlerts}
	 * @memberof PartnerHoldingsAlertsService
	 */
	public getPortfolioSubstitutionAlerts(securitiesTabData: TabData): Array<PortfolioSubstitutionAlerts> {
		const portfolioSubstitutionAlertsList = [];
		const tableData = get(securitiesTabData, 'tableData', []);

		tableData.forEach(table => {
			const invalidSecuritiesSubstitutionMap: Map<string, string[]> = new Map(); // used to get all invalid tickers from all levels that were replaced
			const portfolioSubstitutionAlerts = {
				invalidSecuritiesAlertsMsgMap: new Map<string, string>(),
				assetClassAlertsMsgMap: new Map<string, string>()
			} as PortfolioSubstitutionAlerts;

			const securities = get(table, 'cells', []);
			securities.forEach(security => {
				// Get cell substitutedFrom
				let substitutedFrom: string = this.getCellRawValue(security, 'substitutedFrom');
				const identifier = this.getCellRawValue(security, 'identifier');

				if (!isEmpty(substitutedFrom)) {
					substitutedFrom = typeof substitutedFrom === 'string' ? JSON.parse(substitutedFrom) : substitutedFrom;
					const invalidSecuritiesSubstitution = get(substitutedFrom, 'invalidSecurities');
					const assetClassSubstitution = get(substitutedFrom, 'assetClass');

					if (!isEmpty(invalidSecuritiesSubstitution)) {
						const invalidIdentifierList = invalidSecuritiesSubstitution.map(substitution => substitution.ticker || substitution.cusip);

						// update substitutions based on the current ticker
						const currentValue = invalidSecuritiesSubstitutionMap.get(identifier) || [];
						const updatedValue = currentValue.concat(...invalidIdentifierList);
						invalidSecuritiesSubstitutionMap.set(identifier, updatedValue);
					}

					if (!isEmpty(assetClassSubstitution)) {

						// Create substitution alert messages for asset class
						let assetClassAlertMessage: string = this.contentService.get(
							'ASSET_CLASS_SUBSTITUTION_HOLDING_ALERT',
							'Substitution for: '
						);

						assetClassAlertMessage += assetClassSubstitution.map(substitution => `"${get(substitution, 'description')} (${get(substitution, 'ticker')})"`)
							.join(', ')
							.concat('*');
						portfolioSubstitutionAlerts.assetClassAlertsMsgMap.set(identifier, assetClassAlertMessage);
					}
				}
			});

			this.updateInvalidSecuritySubstitutionAlerts(invalidSecuritiesSubstitutionMap, portfolioSubstitutionAlerts);
			portfolioSubstitutionAlertsList.push(portfolioSubstitutionAlerts);
		});

		return portfolioSubstitutionAlertsList;
	}

	private getMultiPortfolioSubstitutionAlerts(portfolioSubstitutionAlertList: Array<PortfolioSubstitutionAlerts>): MultiPortfolioSubstitutionsAlert {

		const assetClassAlerts: Array<Map<string, string>> = [];
		const invalidSecuritiesAlerts: Array<Map<string, string>> = [];

		portfolioSubstitutionAlertList.forEach(portfolio => {
			assetClassAlerts.push(portfolio.assetClassAlertsMsgMap);
			invalidSecuritiesAlerts.push(portfolio.invalidSecuritiesAlertsMsgMap);
		});
		const multiPortfolioSubstitutionsAlert = { assetClassAlertsMsgMap: assetClassAlerts, invalidSecuritiesAlertsMsgMap: invalidSecuritiesAlerts };
		return multiPortfolioSubstitutionsAlert;
	}

	public formatAlertPercentage(rawValue: number): string | number {
		const splitArray = rawValue.toString().split('.');
		// if rawValue had more than 2 decimals
		if (splitArray[1] && splitArray[1].length > 2) {
			if (splitArray[1].substring(0, 3) === '000') {
				return splitArray[0];
			}
			return rawValue.toFixed(2);
		} else {
			return rawValue;
		}
	}

	public generateAlertRowsForPDF(ticker: string,
		portfolioSubstitutionAlertList: Array<PortfolioSubstitutionAlerts>,
		textSize: string,
		rowColumnProportions: number[],
		portfolioCount: number
	): Array<Array<ColumnComposition>> {

		const alertRowCompositions: Array<Array<ColumnComposition>> = [];
		const alertCellDataRow = [];

		// TODO: tactical fix, column proportions updated to display PDF alerts-
		// comparing 2 portfolios
		if (rowColumnProportions.length === 4) {
			rowColumnProportions = [10, 20, 35, 35];
		}

		// comparing 3 portfolios
		if (rowColumnProportions.length === 5) {
			rowColumnProportions = [10, 20, 30, 20, 20];
		}

		const alertRowCells = Array(portfolioCount).fill(null);

		const alertRequestIdList = [];
		// list of requestIds which has alerts
		portfolioSubstitutionAlertList.forEach((portfolioAlerts: PortfolioSubstitutionAlerts) => {
			alertRequestIdList.push(...Array.from(portfolioAlerts.assetClassAlertsMsgMap.keys()));
			alertRequestIdList.push(...Array.from(portfolioAlerts.invalidSecuritiesAlertsMsgMap.keys()));

		});
		// check if the current ticker has any alerts, if not skip the further calculation of alert row
		const hasTicker = alertRequestIdList.some(reqId => reqId.includes(ticker));
		if (hasTicker) {
			portfolioSubstitutionAlertList.forEach((portfolioAlerts: PortfolioSubstitutionAlerts, index) => {
				// iterate over each alertType keys of portfolio alerts and look for current ticker
				keys(portfolioAlerts).forEach(key => {
					const requestIdKeys: string[] = Array.from(portfolioAlerts[key].keys());
					const requestIdWithCurrentTicker = requestIdKeys.find(id => id.startsWith(`${ticker}@`));
					if (requestIdWithCurrentTicker) {
						alertRowCells[index] = portfolioAlerts[key].get(requestIdWithCurrentTicker);
					}
				});

			});
			if (!isEmpty(alertRowCells)) {
				alertRowCells.forEach(alertMsg => {
					if (alertMsg) {
						alertCellDataRow.push(
							new CellData(null, alertMsg, alertMsg, null, {
								colorTheme: '#FF754E',
								iconType: 'exclamation-triangle'
							})
						);
					} else {
						alertCellDataRow.push(
							new CellData(null, '')
						);
					}
				});
			}
		}

		// Compose list of alert row for PDF for single as well as multiple portfolios
		if (!isEmpty(alertCellDataRow)) {

			const multiPortfolio = rowColumnProportions.length > 3;
			let alertRowColumns: Array<ColumnComposition> = [];

			alertRowColumns = alertCellDataRow.map(
				(alertCellData, alertIndex) =>
					new ColumnComposition({
						cells: alertCellData,
						widthProportion: multiPortfolio ? rowColumnProportions[alertIndex + 2] : 100,
						textClasses: [textSize],
						horizontalAlignment: Alignment.Horizontal.Left,
						verticalAlignment: Alignment.Vertical.Middle,
						renderer: !!alertCellData.rawValue
							? new IconAndTextRenderer(
								alertCellData,
								9,
								9,
								Alignment.Horizontal.Left,
								multiPortfolio ? rowColumnProportions[alertIndex + 2] : 100,
								multiPortfolio ? [10, 90] : [3, 97],
								['text-8', 'text-pale-grey'],
								{},
								null,
								Alignment.Vertical.Top
							)
							: new TextRenderer(
								new CellData(null, '', ''),
								Alignment.Horizontal.Left,
								Alignment.Vertical.Middle,
								multiPortfolio ? rowColumnProportions[alertIndex + 2] : 100
							)
					})
			);

			// If more than one portfolio then add padding to the width equivalent to first two columns
			if (multiPortfolio) {
				const alertPadding = new ColumnComposition({
					cells: new CellData(null, ''),
					widthProportion: rowColumnProportions[0] + rowColumnProportions[1],
					textClasses: [textSize],
					horizontalAlignment: Alignment.Horizontal.Left,
					verticalAlignment: Alignment.Vertical.Middle
				});

				alertRowColumns = [alertPadding, ...alertRowColumns];
			}

			alertRowCompositions.push(alertRowColumns);

			return alertRowCompositions;
		}

		return null;
	}

	private updateInvalidSecuritySubstitutionAlerts(invalidSecuritiesSubstitutionMap: Map<string, string[]>, portfolioSubstitutionAlerts: PortfolioSubstitutionAlerts): void {

		invalidSecuritiesSubstitutionMap.forEach((invalidTickers, substitutionIdentifier) => {
			let alertMessage;
			if (HoldingsUtil.isCashIdentifier(substitutionIdentifier) && invalidTickers.some(ticker => !!ticker)) {
				alertMessage = this.contentService.get(
					'CASH_SUBSTITUTION_HOLDING_ALERT',
					'The following unsupported securities were replaced with cash to analyze this portfolio: '
				);
			} else if (invalidTickers.some(ticker => !!ticker)) {
				alertMessage = this.contentService.get(
					'INVALID_SECURITIES_SUBSTITUTION_HOLDING_ALERT',
					'The following unsupported securities were replaced here to analyze this portfolio: '
				);
			} else {
				alertMessage = this.contentService.get(
					'INVALID_SECURITIES_SUBSTITUTION_DEFAULT_ALERT',
					'Some invalid securities were replaced here to analyze this portfolio'
				);

			}
			const updatedMessage = alertMessage + invalidTickers.join(' ');
			portfolioSubstitutionAlerts.invalidSecuritiesAlertsMsgMap.set(substitutionIdentifier, updatedMessage);
		});
	}
}

export interface MultiPortfolioSubstitutionsAlert {
	assetClassAlertsMsgMap: Map<string, string>[];
	invalidSecuritiesAlertsMsgMap: Map<string, string>[];
}
